import {
  ROTATION_RATE_CAPTION,
  ROTATION_SEED_COST_CAPTION,
  ROTATION_SEED_MIX_LABEL,
  ROTATION_SOWING_DATE_FORMAT,
} from 'constants/uiConstants';
import dayjs from 'dayjs';
import type { RotationMixSeed } from './computeRotationGrid';
import type { RotationCellValues } from './useRotationCellSave';

const rateFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});
const costFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Sowing details as one line, e.g. "80 kg/ha · 12 May · $0.45/kg" or
 * "Seed mix of 3 · 12 May", or null when there are none. The rate is per
 * hectare.
 */
export const formatSowingDetails = (
  sowing: RotationCellValues['sowing'],
  includeCost = false
): string | null => {
  const parts = [
    sowing.mix
      ? ROTATION_SEED_MIX_LABEL(sowing.mix.length)
      : sowing.rate != null
        ? ROTATION_RATE_CAPTION(
            rateFormat.format(sowing.rate),
            sowing.unit?.unit ?? null
          )
        : null,
    sowing.date ? dayjs(sowing.date.slice(0, 10)).format(ROTATION_SOWING_DATE_FORMAT) : null,
    includeCost && !sowing.mix && sowing.cost != null
      ? ROTATION_SEED_COST_CAPTION(
          costFormat.format(sowing.cost),
          sowing.unit?.unit ?? null
        )
      : null,
  ].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/** One seed in a mix, e.g. "Ryegrass · Tetila — 20 kg/ha · $4.50/kg". */
export const formatMixSeed = (seed: RotationMixSeed): string => {
  const name = [seed.cropName, seed.varietyName].filter(Boolean).join(' · ');
  const details = [
    seed.rate != null
      ? ROTATION_RATE_CAPTION(
          rateFormat.format(seed.rate),
          seed.unit?.unit ?? null
        )
      : null,
    seed.cost != null
      ? ROTATION_SEED_COST_CAPTION(
          costFormat.format(seed.cost),
          seed.unit?.unit ?? null
        )
      : null,
  ].filter((part): part is string => !!part);
  return details.length > 0 ? `${name} — ${details.join(' · ')}` : name;
};
